import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  Paper,
  Divider,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import loyaltyService from '../../services/loyaltyService';
import EnrollmentModal from '../common/EnrollmentModal';
import { useAuth } from '../../context/AuthContext';
import usePointsLabel from '../../hooks/usePointsLabel';
import useBrandPinIcon from '../../hooks/useBrandPinIcon';
import RecordSection from '../records/RecordSection';
import { RECORD_SECTIONS } from '../records/sectionTitles';
import { hidesZeroBalance } from './balance';
import StoreCard from '../locations/StoreCard';
import StorePopup from '../locations/StorePopup';
import { calculateMapBounds, storeCardRows, withDistances } from '../locations/storeLocations';

// Rows each record section shows here; the rest are a "See all" away on the section's own page.
const RECORD_PREVIEW_LIMIT = 5;

// The layout before sections were orderable. Used when a group's payload has no portal_records.
const DEFAULT_SECTION_ORDER = ['balance', 'offers', 'store_locations'];

// Store cards shown here; "See all" opens the group's full locations page (list beside a map), which holds up for a
// program with hundreds of stores where an expanding grid would not.
const NEAREST_LOCATIONS_SHOWN = 6;

const MembershipDetails = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const { org } = useAuth();
  const [group, setGroup] = useState(null);
  const [availableOffers, setAvailableOffers] = useState([]);
  const [earnableOffers, setEarnableOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [redeemModalOpen, setRedeemModalOpen] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [redeeming, setRedeeming] = useState(false);
  const [redeemError, setRedeemError] = useState(null);
  const [redeemSuccess, setRedeemSuccess] = useState(null);
  const [countdown, setCountdown] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [locationsWithDistance, setLocationsWithDistance] = useState([]);
  const [userLocation, setUserLocation] = useState(null);
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [leaveError, setLeaveError] = useState(null);
  const [isMember, setIsMember] = useState(true);
  const [enrollmentModal, setEnrollmentModal] = useState({ open: false, location: null });
  const points = usePointsLabel(group);
  const pinIcon = useBrandPinIcon();

  useEffect(() => {
    if (redeemSuccess && countdown === null) {
      setCountdown(10);
    }
  }, [redeemSuccess, countdown]);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  useEffect(() => {
    if (redeemSuccess && redeemModalOpen) {
      const timer = setInterval(() => {
        setCurrentTime(new Date());
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [redeemSuccess, redeemModalOpen]);
  const handleEnrollClick = (location) => {
    setEnrollmentModal({ open: true, location });
  };

  const handleEnrollSuccess = async () => {
    // Refresh the membership details after successful enrollment
    await fetchCompanyDetails();
  };

  const fetchCompanyDetails = async () => {
    try {
      setLoading(true);
      // Fetch group details directly using the new endpoint
      const response = await loyaltyService.getGroupDetails(companyId);

      if (!response) {
        throw new Error('Membership not found');
      }

      setIsMember(response.group.member_info.is_member);
      setGroup(response.group);
      setAvailableOffers(response.group.offers.filter(offer => !offer.point_value || response.group.member_info.current_points >= offer.point_value));
      setEarnableOffers(response.group.offers.filter(offer => offer.point_value && response.group.member_info.current_points < offer.point_value));
      setLoading(false);
    } catch (err) {
      // Check if it's a 404 error
      if (err.response && err.response.status === 404) {
        setError('Unable to find loyalty program');
      } else {
        setError(err.message || 'Failed to load membership details');
      }
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanyDetails();
    // Get user's current location
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lon: position.coords.longitude
          });
        },
        (error) => {
          console.log('Could not get user location:', error);
        }
      );
    }
  }, [companyId]);

  useEffect(() => {
    // Nearest first once the member's position is known; the server's order until then.
    if (group?.locations) setLocationsWithDistance(withDistances(group.locations, userLocation));
  }, [group, userLocation]);

  const handleBack = () => {
    navigate('/memberships');
  };

  const handleRedeemClick = (offer) => {
    setSelectedOffer(offer);
    setRedeemModalOpen(true);
    setRedeemError(null);
    setRedeemSuccess(null);
  };

  const handleCloseModal = () => {
    // Prevent closing during countdown
    if (countdown > 0) return;

    setRedeemModalOpen(false);
    setSelectedOffer(null);
    setRedeemError(null);
    setRedeemSuccess(null);
    setRedeeming(false);
    setCountdown(null);
  };

  const handleLeaveProgramClick = () => {
    setLeaveDialogOpen(true);
    setLeaveError(null);
  };

  const handleCloseLeaveDialog = () => {
    setLeaveDialogOpen(false);
    setLeaveError(null);
  };

  const handleConfirmLeave = async () => {
    setLeaving(true);
    setLeaveError(null);

    try {
      await loyaltyService.leaveProgram(group.id);
      // Navigate back to dashboard after successful leave
      navigate('/memberships');
    } catch (err) {
      setLeaveError(err.response?.data?.message || err.response?.data?.error || 'Failed to leave loyalty program');
    } finally {
      setLeaving(false);
    }
  };

  const handleConfirmRedeem = async () => {
    if (!selectedOffer) return;

    setRedeeming(true);
    setRedeemError(null);

    let location = null;
    if (selectedOffer.requires_location) {
      try {
        location = await getDeviceLocation();
      } catch (locationError) {
        setRedeemError(locationError.message);
        setRedeeming(false);
        return;
      }
    }

    try {
      const response = await loyaltyService.redeemCoupon(group.id, selectedOffer.id, selectedOffer.customer_coupon_id, location);
      fetchCompanyDetails();
      setRedeemSuccess(response.redemption);

      // Update points locally immediately
      if (response.redemption.point_value) {
        setGroup(prevGroup => ({
          ...prevGroup,
          member_info: {
            ...prevGroup.member_info,
            current_points: prevGroup.member_info.current_points - response.redemption.point_value
          }
        }));
      } else if (selectedOffer.customer_coupon_id) {
        // Remove the redeemed offer from available offers
        setAvailableOffers(prevOffers => prevOffers.filter(offer => offer.customer_coupon_id !== selectedOffer.customer_coupon_id));
      }
    } catch (err) {
      setRedeemError(err.response?.data?.message || err.response?.data?.error || 'Failed to redeem coupon');
    } finally {
      setRedeeming(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md">
        <Box sx={{ my: 4 }}>
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="h5" gutterBottom>
              {error}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              The loyalty program you're looking for may have been removed or you may not have access to it.
            </Typography>
            <Button variant="contained" onClick={handleBack}>
              Back to Memberships
            </Button>
          </Paper>
        </Box>
      </Container>
    );
  }

  const portalSections = group.portal_records?.sections || {};
  // The server sends only sections that are on and available, already in order.
  const sectionOrder = group.portal_records?.order || DEFAULT_SECTION_ORDER;
  // First in the order keeps the balance beside the group name, where it has always been.
  const balanceInHeader = sectionOrder[0] === 'balance';
  const showBalance = isMember && !hidesZeroBalance(group.hide_zero_balance, group.member_info?.current_points);
  const mapBounds = calculateMapBounds(locationsWithDistance);
  const locationRows = storeCardRows(locationsWithDistance);

  const renderBalance = () =>
    showBalance ? (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <MonetizationOnIcon sx={{ color: 'gold', mr: 1 }} />
        <Typography variant="h5">{points.format(group.member_info.current_points)}</Typography>
      </Box>
    ) : null;

  const renderOffers = () => (
    <>
        {availableOffers.length > 0 && (
          <>
            <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
              Available Offers
            </Typography>

            <Grid container spacing={2}>
              {availableOffers.map((offer) => (
                <Grid size={{ xs: 12, md: 6 }} key={offer.id}>
                  <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Typography variant="h6" component="div">
                          {offer.name}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                          {!!offer.point_value && (
                            <>
                              <MonetizationOnIcon sx={{ color: 'primary.main', fontSize: 20, mr: 0.5 }} />
                              <Typography variant="body2" color="primary" fontWeight="bold">
                                {points.format(offer.point_value)}
                              </Typography>
                            </>)}
                        </Box>
                      </Box>

                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, flexGrow: 1 }}>
                        {offer.description}
                      </Typography>

                      <Box sx={{ mt: 'auto' }}>

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box>
                            {offer.days_remaining && (
                              <Typography variant="caption" color="warning.main" display="block" sx={{ mb: 1 }}>
                                {offer.days_remaining} days remaining. Expires on {new Date(offer.expire_when).toLocaleDateString()}
                              </Typography>
                            )}
                          </Box>

                          {group.allow_self_redemption && (
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => handleRedeemClick(offer)}
                            >
                              Redeem
                            </Button>
                          )}
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </>
        )}

        {earnableOffers.length > 0 && (
          <>
            <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
              Earnable Offers
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Earnable offers are subject to change without notice. Please check back frequently for new offers.
            </Typography>

            <Grid container spacing={2}>
              {earnableOffers.map((offer) => (
                <Grid size={{ xs: 12, md: 6 }} key={offer.id}>
                  <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Typography variant="h6" component="div" sx={{ color: '#999' }}>
                          {offer.name}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                          <MonetizationOnIcon sx={{ color: 'warning.main', fontSize: 20, mx: 0.5 }} />
                          <Typography variant="body2" color="warning.main" fontWeight="bold">
                            {points.format(offer.point_value)}
                          </Typography>
                        </Box>
                      </Box>

                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, flexGrow: 1 }}>
                        {offer.description}
                      </Typography>

                      <Box sx={{ mt: 'auto' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box>
                            {offer.days_remaining && (
                              <Typography variant="caption" color="warning.main" display="block" sx={{ mb: 1 }}>
                                {offer.days_remaining} days remaining. Expires on {new Date(offer.expire_when).toLocaleDateString()}
                              </Typography>
                            )}

                            <Typography variant="caption" color="warning.main" display="block" sx={{ mb: 1 }}>
                              You need to earn {Math.ceil(offer.point_value - group.member_info.current_points).toLocaleString()} more {points.word(Math.ceil(offer.point_value - group.member_info.current_points))} to redeem this offer.
                            </Typography>
                          </Box>

                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </>
        )}
    </>
  );

  const renderStoreLocations = () => (
    <>
        {/* Locations Map Section */}
        {locationsWithDistance && locationsWithDistance.length > 0 && (
          <>
            <Typography variant="h5" gutterBottom sx={{ mt: 4, mb: 2 }}>
              Store Locations
            </Typography>

            {/* Without coordinates there is nothing to map ("Invalid LatLng (0, NaN)"); the list below still shows. */}
            {mapBounds && (
            <Box sx={{ height: { xs: 260, sm: 400 } }}>
              <MapContainer
                bounds={mapBounds}
                // One location gives zero-area bounds, and the map is fitted before its tile layer sets a max zoom;
                // capping the fit keeps it from zooming in without limit.
                boundsOptions={{ maxZoom: 15 }}
                style={{ height: '100%', width: '100%', borderRadius: '4px' }}
                scrollWheelZoom={false}
                // On a phone a one-finger drag pans the map instead of scrolling the page, which traps a member
                // mid-page now that companies can move this section up. Pinch and the +/- buttons still zoom.
                dragging={!L.Browser.mobile}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />

                {locationsWithDistance.filter((location) => location.lat && location.lon).map((location) => (
                  <Marker
                    key={location.id}
                    position={[location.lat, location.lon]}
                    icon={pinIcon}
                    // Names the pin for screen readers: a div icon has no alt text, unlike the old PNG pin.
                    title={location.friendly_name || location.name}
                  >
                    <Popup>
                      <StorePopup location={location} />
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </Box>
            )}

            {/* The nearest few, each card the same size; "See all" opens the full list beside a map. */}
            <Grid container spacing={2} sx={{ mt: 2 }}>
              {locationsWithDistance.slice(0, NEAREST_LOCATIONS_SHOWN).map((location) => (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={location.id}>
                  <StoreCard location={location} rows={locationRows} />
                </Grid>
              ))}
            </Grid>
            {locationsWithDistance.length > NEAREST_LOCATIONS_SHOWN && (
              <Button onClick={() => navigate(`/memberships/${group.id}/locations`)} sx={{ mt: 1 }}>
                See all {locationsWithDistance.length} locations
              </Button>
            )}
          </>
        )}
    </>
  );

  return (
    <Container maxWidth="md">
      <Box sx={{ my: 4 }}>
        {!org && (
          <IconButton onClick={handleBack} sx={{ mb: 2 }}>
            <ArrowBackIcon />
          </IconButton>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
          {group.logo && (
            <Box
              component="img"
              src={group.logo}
              alt={`${group.name} logo`}
              sx={{
                width: 100,
                height: 100,
                objectFit: 'contain',
                mr: 3
              }}
            />
          )}
          <Box>
            <Typography variant="h4" gutterBottom>
              {group.name} Membership
            </Typography>
            {isMember ? (
              balanceInHeader && renderBalance()
            ) : (
              <Button
                variant="contained"
                onClick={() => handleEnrollClick(group)}
              >
                Sign up now
              </Button>
            )}
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Every section renders in the order the company chose on the Loyalty settings page. */}
        {sectionOrder.map((sectionKey) => {
          if (sectionKey === 'balance') {
            return balanceInHeader ? null : <Box key="balance" sx={{ mt: 3 }}>{renderBalance()}</Box>;
          }
          if (sectionKey === 'offers') return <React.Fragment key="offers">{renderOffers()}</React.Fragment>;
          if (sectionKey === 'store_locations') {
            return <React.Fragment key="store_locations">{renderStoreLocations()}</React.Fragment>;
          }
          // Records belong to members only; a visitor who hasn't joined sees the program, not anyone's data.
          if (RECORD_SECTIONS.includes(sectionKey) && isMember) {
            return (
              <RecordSection
                key={sectionKey}
                groupId={group.id}
                sectionKey={sectionKey}
                section={portalSections[sectionKey]}
                previewLimit={RECORD_PREVIEW_LIMIT}
                onSeeAll={() => navigate(`/memberships/${group.id}/${sectionKey}`)}
              />
            );
          }
          return null;
        })}

        {/* Redeem Warning Modal. No onClose, and Escape is off: only the dialog's own Cancel / Close buttons close it.
            WHY: after redeeming, this is the screen the member shows the cashier and the coupon is already used up, so a
            stray tap outside the box (or Escape) must not dismiss it. Product decision, CIN-7192. */}
        <Dialog
          open={redeemModalOpen}
          maxWidth="sm"
          fullWidth
          disableEscapeKeyDown
        >
          <DialogTitle>
            <Typography variant="h6" color={redeemSuccess ? 'primary.main' : 'default'}>
              {redeemSuccess ? 'Coupon Redeemed Successfully. Show device to cashier.' : 'Redeem Offer'}
            </Typography>
          </DialogTitle>
          <DialogContent>
            {redeemSuccess ? (
              // Success content
              <Box>

                {selectedOffer && (
                  <Card variant="outlined" sx={{ mb: 2 }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Typography variant="h6" component="div">
                          {selectedOffer.name}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <MonetizationOnIcon sx={{ color: 'gold', mr: 0.5 }} />
                          <Typography variant="body1" fontWeight="bold">
                            {selectedOffer.point_value}
                          </Typography>
                        </Box>
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {selectedOffer.description}
                      </Typography>
                      {selectedOffer.cashier_directions && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body1" fontWeight="bold">
                            Cashier Instructions:
                          </Typography>
                          <Typography variant="body2">
                            {selectedOffer.cashier_directions}
                          </Typography>
                        </Box>
                      )}
                      {selectedOffer.cashier_image && (
                        <Box>
                          <Box
                            component="img"
                            src={selectedOffer.cashier_image}
                            alt="Show this to the cashier"
                            sx={{ display: 'block', maxWidth: '100%', height: 'auto' }}
                          />
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                )}

                <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                  {redeemSuccess.redeemed_when && (
                    <Typography variant="body2" gutterBottom>
                      <strong>Redeemed:</strong> {new Date(redeemSuccess.redeemed_when).toLocaleString()}
                    </Typography>
                  )}
                  <Typography variant="body2" gutterBottom>
                    <strong>Current Time:</strong> {currentTime.toLocaleString()}
                  </Typography>
                </Box>
                {countdown > 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
                    You can close this window in {countdown} seconds
                  </Typography>
                )}
              </Box>
            ) : (
              // Warning/Error content
              <>
                <Typography variant="body1" color="error" fontWeight="bold" gutterBottom>
                  This coupon MUST be redeemed in front of the cashier for this coupon to be valid
                </Typography>
                {selectedOffer && (
                  <Box sx={{ mt: 2 }}>
                    <Card variant="outlined" sx={{ mb: 2 }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                          <Typography variant="h6" component="div">
                            {selectedOffer.name}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {!!selectedOffer.point_value && (<>
                              <MonetizationOnIcon sx={{ color: 'gold', mr: 0.5 }} />
                              <Typography variant="body1" fontWeight="bold">
                                {selectedOffer.point_value}
                              </Typography>
                            </>)}
                          </Box>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {selectedOffer.description}
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box>
                            {selectedOffer.days_remaining && (
                              <Typography variant="caption" color="warning.main" display="block">
                                {selectedOffer.days_remaining} days remaining. Expires on {new Date(selectedOffer.expire_when).toLocaleDateString()}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                    {!!selectedOffer.point_value && (
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                        Your account will be deducted {points.format(selectedOffer.point_value)}
                      </Typography>
                    )}
                  </Box>
                )}
                {redeemError && (
                  <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                    Error: {redeemError}
                  </Typography>
                )}
              </>
            )}
          </DialogContent>
          <DialogActions>
            {redeemSuccess ? (
              <Button
                variant="contained"
                onClick={handleCloseModal}
                disabled={countdown > 0}
              >
                {countdown > 0 ? `Close (${countdown})` : 'Close'}
              </Button>
            ) : (
              <>
                <Button onClick={handleCloseModal} disabled={redeeming}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleConfirmRedeem}
                  disabled={redeeming}
                >
                  {redeeming ? 'Redeeming...' : 'I Understand'}
                </Button>
              </>
            )}
          </DialogActions>
        </Dialog>

        {/* Leave Program Dialog */}
        <Dialog
          open={leaveDialogOpen}
          onClose={handleCloseLeaveDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Typography variant="h6" color="error">
              Leave Loyalty Program
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1" gutterBottom>
              Are you sure you wish to leave this loyalty program?  Your contact information will be removed from the program, and you will no longer receive any offers or rewards.
            </Typography>
            <Typography variant="body1" color="error" fontWeight="bold">
              You will immediately forfeit all rewards and {points.plural} earned.
            </Typography>
            {leaveError && (
              <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                Error: {leaveError}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseLeaveDialog} disabled={leaving}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleConfirmLeave}
              disabled={leaving}
            >
              {leaving ? 'Leaving...' : 'Leave Program'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Leave Program Link */}
        {isMember && (
          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Link
              component="button"
              variant="body2"
              color="error"
              onClick={handleLeaveProgramClick}
              sx={{ fontSize: '0.875rem' }}
            >
              Leave Loyalty Program
            </Link>
          </Box>
        )}

        {/* Enrollment Modal */}
        <EnrollmentModal
          open={enrollmentModal.open}
          onClose={() => setEnrollmentModal({ open: false, location: null })}
          location={enrollmentModal.location}
          onSuccess={handleEnrollSuccess}
        />

      </Box>
    </Container>
  );
};

// Helper function to calculate bounds for all locations
const getDeviceLocation = () => new Promise((resolve, reject) => {
  if (!navigator.geolocation) {
    reject(new Error('Your browser does not support location services, which are required to redeem this coupon.'));
    return;
  }
  navigator.geolocation.getCurrentPosition(
    // accuracy (meters) is recorded with the redemption so staff can tell a GPS fix from a coarse network guess.
    (position) => resolve({ lat: position.coords.latitude, lon: position.coords.longitude, accuracy: position.coords.accuracy }),
    (error) => {
      if (error.code === error.PERMISSION_DENIED) {
        reject(new Error('Location services must be enabled to redeem this coupon. Allow location access for this site in your browser settings and try again.'));
      } else {
        reject(new Error('We could not get your location. Please make sure location services are turned on and try again.'));
      }
    },
    { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
  );
});

export default MembershipDetails;